<div class="flex gap-4" [ngClass]="vertical() ? 'flex-col' : 'flex-col sm:flex-row'">
  <!-- LEFT: Drop zone or pending files -->
  @if (!readonly()) {
    <div [ngClass]="vertical() ? 'w-full' : 'w-full sm:w-76 shrink-0'">
      @if (uploadedFiles().length === 0) {
        <!-- Empty drop zone -->
        <label
          class="flex flex-col items-center justify-center gap-2 h-40 w-full rounded-2xl border-2 border-dashed cursor-pointer transition-colors"
          [ngClass]="
            isDragOver()
              ? 'border-accent-400 bg-accent-50'
              : 'border-accent-200 bg-accent-50 hover:border-accent-400 hover:bg-accent-500/6'
          "
          (dragover)="$event.preventDefault(); isDragOver.set(true)"
          (dragleave)="isDragOver.set(false)"
          (drop)="onFileDrop($event)"
        >
          <input
            type="file"
            class="hidden"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
            (change)="onFileSelect($event)"
          />
          <img src="/icons/upload-icon.svg" class="w-8 h-8" alt="" />
          <p class="text-xs text-slate-500 text-center px-2">
            <span class="font-semibold text-accent-500">Drag &amp; Drop</span><br />
            or <span class="text-accent-500 underline">browse</span> your files
          </p>
          <p class="text-2xs text-slate-400">PDF, DOCX, PNG, JPG · max 10 MB</p>
        </label>
      } @else {
        <!-- Pending files box -->
        <div
          class="rounded-2xl border-2 border-dashed border-accent-200 bg-accent-50 px-4 py-5 flex flex-col gap-5"
          (dragover)="$event.preventDefault(); isDragOver.set(true)"
          (dragleave)="isDragOver.set(false)"
          (drop)="onFileDrop($event)"
        >
          @for (uf of uploadedFiles(); track $index; let i = $index; let last = $last) {
            <div class="flex flex-col items-center gap-2.5 relative">
              <!-- Remove (not while uploading) -->
              @if (uf.status !== 'uploading') {
                <button
                  type="button"
                  (click)="removeFile(i)"
                  class="absolute -top-1 -right-1 text-slate-300 hover:text-red-400 transition-colors cursor-pointer"
                >
                  <img src="/icons/x-icon.svg" class="w-3.5 h-3.5" alt="Remove" />
                </button>
              }

              <img src="/icons/file-icon.svg" class="w-7 h-7" alt="" />
              <div class="text-center w-full">
                <p class="text-xs font-semibold text-navy-900 truncate">{{ uf.name }}</p>
                <p class="text-2xs text-slate-400 mt-0.5">{{ uf.sizeLabel }}</p>
              </div>

              @if (uf.validationError) {
                <!-- Validation error with toggle -->
                <button
                  type="button"
                  (click)="toggleDoc('err-' + i)"
                  class="flex items-center gap-1 text-2xs text-red-400 cursor-pointer"
                >
                  File not allowed
                  <img
                    src="/icons/chevron-down-icon.svg"
                    class="w-3 h-3 transition-transform duration-150"
                    [ngClass]="expandedDocs().has('err-' + i) ? 'rotate-180' : ''"
                    alt=""
                  />
                </button>
                @if (expandedDocs().has('err-' + i)) {
                  <p
                    class="text-2xs text-red-400 text-center bg-red-50 rounded-lg px-2 py-1.5 w-full"
                  >
                    {{ uf.validationError }}
                  </p>
                }
              } @else if (uf.status === 'uploading') {
                <p class="text-2xs text-accent-500">Uploading...</p>
              } @else if (uf.status === 'error') {
                <!-- Upload error with toggle -->
                <button
                  type="button"
                  (click)="toggleDoc('uperr-' + i)"
                  class="flex items-center gap-1 text-2xs text-red-400 cursor-pointer"
                >
                  Upload failed
                  <img
                    src="/icons/chevron-down-icon.svg"
                    class="w-3 h-3 transition-transform duration-150"
                    [ngClass]="expandedDocs().has('uperr-' + i) ? 'rotate-180' : ''"
                    alt=""
                  />
                </button>
                @if (expandedDocs().has('uperr-' + i)) {
                  <div
                    class="flex flex-col items-center gap-1.5 w-full bg-red-50 rounded-lg px-2 py-2"
                  >
                    <p class="text-2xs text-red-400 text-center">
                      Could not save the document. Try again.
                    </p>
                    <button
                      type="button"
                      (click)="triggerUpload(i)"
                      class="text-2xs font-semibold text-accent-500 underline cursor-pointer"
                    >
                      Retry
                    </button>
                  </div>
                }
              } @else {
                <!-- Pending: type selector + save button -->
                <app-select
                  placeholder="Select document type"
                  [options]="documentTypeOptions()"
                  [value]="uf.documentTypeId"
                  [borderless]="true"
                  (changed)="onTypeSelected(i, $event)"
                />
                @if (uf.documentTypeId) {
                  <button
                    type="button"
                    (click)="triggerUpload(i)"
                    class="w-full py-2 rounded-xl bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    Save document
                  </button>
                }
              }
            </div>

            @if (!last) {
              <div class="border-t border-surface-400 -mx-4"></div>
            }
          }

          <!-- Add more -->
          <label
            class="flex items-center justify-center gap-1 text-2xs text-accent-500 hover:text-accent-600 cursor-pointer transition-colors mt-1"
          >
            <input
              type="file"
              class="hidden"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
              (change)="onFileSelect($event)"
            />
            <span class="text-sm leading-none font-light">+</span> Add more files
          </label>
        </div>
      }
    </div>
  }

  <!-- RIGHT: Saved documents (with toggle) -->
  <div class="flex flex-col gap-2 flex-1 min-w-0">
    @for (doc of existingDocuments(); track doc.id) {
      <div class="rounded-xl border border-surface-400 bg-white overflow-hidden">
        <!-- Row header — always visible, click to toggle -->
        <button
          type="button"
          (click)="toggleDoc(doc.id)"
          class="w-full flex items-center gap-3 px-3 py-2.5 text-left cursor-pointer hover:bg-slate-50 transition-colors"
        >
          <img src="/icons/file-icon.svg" class="w-4 h-4 shrink-0" alt="" />
          <div class="flex-1 min-w-0">
            <p class="text-xs font-medium text-navy-900 truncate">{{ doc.name }}</p>
            <p class="text-2xs text-slate-400">{{ doc.type }}</p>
          </div>
          <img
            src="/icons/chevron-down-icon.svg"
            class="w-3.5 h-3.5 shrink-0 opacity-40 transition-transform duration-200"
            [ngClass]="expandedDocs().has(doc.id) ? 'rotate-180' : ''"
            alt=""
          />
        </button>

        <!-- Expanded details -->
        @if (expandedDocs().has(doc.id)) {
          <div
            class="border-t border-surface-400 px-3 py-2.5 flex items-center justify-between gap-3"
          >
            <div class="flex flex-col gap-1">
              <p class="text-2xs text-slate-400">Document type</p>
              <p class="text-xs font-medium text-navy-900">{{ doc.type }}</p>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              <button
                type="button"
                (click)="onView(doc.documentUrl)"
                [disabled]="!doc.documentUrl"
                class="text-2xs font-medium text-accent-500 hover:text-accent-700 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                View
              </button>
              @if (!readonly()) {
                <button
                  type="button"
                  (click)="onDelete(doc.id)"
                  [disabled]="deletingIds().has(doc.id)"
                  class="text-2xs font-medium text-red-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ deletingIds().has(doc.id) ? 'Deleting…' : 'Delete' }}
                </button>
              }
            </div>
          </div>
        }
      </div>
    }

    @if (existingDocuments().length === 0 && uploadedFiles().length === 0) {
      <div class="flex items-center justify-center h-full text-2xs text-slate-400 py-6">
        No files attached yet.
      </div>
    }
  </div>
</div>
